import { default as React, useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import {
  AlertPriority,
  AlertToken,
  PresentationType,
} from "../../redux/alerts";
import {
  alertsAfterGateById,
  remindersAfterGateById,
  residentCardColor,
  sortAlertsAndRemindersForCard,
} from "../../redux/alerts/selectors";
import { DeviceType } from "../../redux/devices";
import { doesAnyDeviceHasProblemCreator } from "../../redux/devicesStatuses/selectors";
import { DeviceMinimizedMap, presenceStatuses } from "../../redux/residents";
import {
  residentDevicesAfterGateCreator,
  residentsCreator,
} from "../../redux/residents/selectors";
import { IAppState } from "../../redux/types";
import { alertIconToken } from "../../utils/AlertTo";
import { AvatarResidentUser } from "../common/AvatarUser";
import { OverflowTip, RoomBedDisplayByMin } from "../common/Common";
import { ModalTypes } from "./Callbacks";
import {
  MarkForBed,
  MarkForNearRoom,
  MarkForResidentActivityStatus,
  MarkForResidentAvatar,
} from "./utils";

const OneStatusView = ({
  residentId,
  callback,
  index,
}: {
  residentId: string;
  callback: (...args: any) => void;
  index: number;
}): JSX.Element => {
  const ResidentSelection = useMemo(() => residentsCreator(), []);
  const resident = useSelector((state: IAppState) =>
    ResidentSelection(state, residentId)
  );
  if (resident)
    return (
      <CardInner residentId={residentId} callback={callback} index={index} />
    );
  return null;
};
const CardInner = ({
  residentId,
  callback,
  index,
}: {
  residentId: string;
  callback: (...args: any) => void;
  index: number;
}): JSX.Element => {
  // console.log('oneCard');
  const ResidentSelection = useMemo(() => residentsCreator(), []);
  const resident = useSelector((state: IAppState) =>
    ResidentSelection(state, residentId)
  );

  const devicesSelection = useMemo(() => residentDevicesAfterGateCreator(), []);
  const residentDevices = useSelector((state: IAppState) =>
    devicesSelection(state, residentId)
  );
  const { id } = resident;

  const selection2 = useMemo(() => residentCardColor(), []);
  const colorPriority = useSelector((state: IAppState) =>
    selection2(state, residentId)
  );

  const devisesAbsent = resident.devices.length === 0;

  const onClickAlerts = useCallback(() => {
    callback(ModalTypes.SHOW_FULL_RESIDENT_CARD, residentId);
  }, [residentId, callback]);

  const onClickDeviceStatuses = useCallback(
    (e: any) => {
      e.preventDefault();
      e.stopPropagation();
      callback(ModalTypes.SHOW_DEVICES_STATUSES, id);
    },
    [id, callback]
  );
  const onShowResidentStatus = useCallback(
    (event: React.MouseEvent<Element, MouseEvent>) => {
      event.stopPropagation();
      callback(ModalTypes.SHOW_RESIDENT_STATUSES, id);
    },
    [id, callback]
  );

  const onShowUserNearRoomStatus = useCallback(
    (event: React.MouseEvent<Element, MouseEvent>) => {
      event.stopPropagation();
      callback(ModalTypes.SHOW_USER_NEAR_ROOM_STATUSES, id);
    },
    [id, callback]
  );

  const cardClassName = `card ${
    colorPriority === AlertPriority.high
      ? "red"
      : colorPriority === AlertPriority.low
      ? "yellow"
      : colorPriority === AlertPriority.med
      ? "black"
      : ""
  }`;

  console.log("residentCard");
  const hasOnlyWatch =
    Object.keys(residentDevices).find(
      (el) => residentDevices[el].type === DeviceType.watch
    ) && Object.keys(residentDevices).length === 1;
  const { presentation } = useSelector(
    (state: IAppState) => state.alerts.settings
  );
  console.log(presentation);

  return presentation === PresentationType.Cards ? (
    <div
      className={`one-notif-${cardClassName} card-view flex-column ${
        presentation === 0 ? "card-height" : ""
      }`}
      onClick={onClickAlerts}
      style={{ marginTop: index === 0 ? "" : "10px" }}
    >
      <RoomBedDisplayByMin devicesMinimized={resident.devicesMinimized} />
      <div className="flex-row flex-row-8">
        {!hasOnlyWatch ? (
          <div className="flex-column" id="posture-data">
            {MarkForNearRoom({ residentId: residentId }) && (
              <span
                onClick={(e) => onShowUserNearRoomStatus(e)}
                className="bed-holder"
                style={{ display: "flex" }}
              >
                <MarkForNearRoom residentId={residentId} />
              </span>
            )}
            <span
              onClick={(e) => onShowResidentStatus(e)}
              style={{ display: "flex" }}
            >
              <MarkForBed residentId={residentId} />
            </span>
          </div>
        ) : (
          <i className={`icon-info icon-big icon-device-watch `} />
        )}
        <AvatarResidentUser size="68" profile={resident} view={"card"}>
          <MarkForResidentAvatar resident={resident} />
        </AvatarResidentUser>
        {!devisesAbsent && (
          <ResidentDevicesStatuses
            isAway={resident.nodePresence === presenceStatuses.away}
            devicesMinimized={residentDevices}
            residentId={residentId}
            onClickDeviceStatuses={onClickDeviceStatuses}
          />
        )}
      </div>
      <OverflowTip text={[resident.firstName, resident.lastName].join(" ")} />

      <AlertsStack
        onClickAlerts={onClickAlerts}
        residentId={residentId}
        size="32"
        view={"card"}
      />
    </div>
  ) : presentation === PresentationType.List ? (
    <div
      className={`one-notif-${cardClassName} list-view flex-row`}
      onClick={onClickAlerts}
      style={{ marginTop: index === 0 ? "" : "10px" }}
    >
      <AvatarResidentUser size="48" profile={resident}>
        <MarkForResidentAvatar resident={resident} />
      </AvatarResidentUser>
      <div className="flex-column">
        <RoomBedDisplayByMin devicesMinimized={resident.devicesMinimized} />
        <div className="flex-row">
          {MarkForNearRoom({ residentId: residentId }) && (
            <span
              onClick={onShowUserNearRoomStatus}
              className="bed-holder"
              style={{ display: "flex" }}
            >
              <MarkForNearRoom residentId={residentId} size={16} />
            </span>
          )}
          <span onClick={onShowResidentStatus} style={{ display: "flex" }}>
            <MarkForBed residentId={residentId} size={16} />
          </span>
          <ResidentDevicesStatuses
            isAway={resident.nodePresence === presenceStatuses.away}
            devicesMinimized={residentDevices}
            residentId={residentId}
            onClickDeviceStatuses={onClickDeviceStatuses}
            size={16}
          />
          <div>
            <AlertsStack
              onClickAlerts={onClickAlerts}
              residentId={residentId}
              size="16"
              spliceTo={3}
              tile={"tile"}
            />
          </div>
        </div>
      </div>
    </div>
  ) : (
    <div
      className={`one-notif-${cardClassName} tile-view flex-row`}
      onClick={onClickAlerts}
    >
      <AvatarResidentUser profile={resident} size={"68"} view={"tile"}>
        <div className="status-holder-alert-top">
          <AlertsStack
            onClickAlerts={onClickAlerts}
            residentId={residentId}
            size="24"
            spliceTo={1}
          />
        </div>
      </AvatarResidentUser>
      <div className="status-panel">
        {MarkForBed({ residentId: residentId }) && (
          <span
            onClick={onShowResidentStatus}
            className="bed-holder"
            style={{ display: "flex" }}
          >
            <MarkForBed residentId={residentId} />
          </span>
        )}

        {MarkForNearRoom({ residentId: residentId }) && (
          <span
            onClick={onShowUserNearRoomStatus}
            className="bed-holder"
            style={{ display: "flex" }}
          >
            <MarkForNearRoom residentId={residentId} />
          </span>
        )}
        {!devisesAbsent && (
          <ResidentDevicesStatuses
            isAway={resident.nodePresence === presenceStatuses.away}
            devicesMinimized={residentDevices}
            residentId={residentId}
            onClickDeviceStatuses={onClickDeviceStatuses}
          />
        )}
        {resident.nodePresence === presenceStatuses.away ? (
          <div className="status-holder">
            <i className="icon-status-away">
              <span className="path1"></span>
              <span className="path2"></span>
              <span className="path3"></span>
            </i>
          </div>
        ) : (
          <MarkForResidentActivityStatus devices={residentDevices} />
        )}
      </div>
    </div>
  );
};

export const AlertsStack = ({
  residentId,
  onClickAlerts,
  size = "32",
  spliceTo = 5,
  tile,
  view,
  list,
}: {
  residentId: string;
  onClickAlerts: () => void;
  size?: string;
  spliceTo?: number;
  tile?: string;
  view?: string;
  list?: string;
}) => {
  const alerts = useSelector((state: IAppState) =>
    alertsAfterGateById(state, residentId)
  );
  const reminders = useSelector((state: IAppState) =>
    remindersAfterGateById(state, residentId)
  );
  // const together = [...alerts, ...(reminders || [])];
  // const togetherSorted = together.sort(sortAlertsAndRemindersForCard);
  const togetherSorted = useMemo(() => {
    const together = [...alerts, ...(reminders || [])];
    return [...together].sort(sortAlertsAndRemindersForCard);
  }, [alerts, reminders]);
  return togetherSorted.length > 0 ? (
    <div className="flex-row alerts" onClick={onClickAlerts}>
      {togetherSorted.slice(0, spliceTo).map((el, index) => {
        const isReminder = "activityType" in el;
        return (
          <div
            className={`alert-${size} ${
              view === "card" && togetherSorted.length > 2 ? "alert-mr" : ""
            }`}
            key={index}
            style={{
              borderRadius: view === "card" || tile === "tile" ? "50%" : "",
              border:
                view === "card" || tile === "tile" ? "2px solid #242424" : "",
            }}
          >
            {!isReminder ? (
              alertIconToken(el["alertToken"] as AlertToken)
            ) : (
              <i
                className={`icon-alert info icon-action-${el["activityType"]}`}
              />
            )}
            {togetherSorted.length > spliceTo &&
              spliceTo !== 1 &&
              index === spliceTo - 1 && (
                <div className={`${tile ? "badge-status" : "badge"}`}>
                  {tile ? "" : "+"}
                  {togetherSorted.length - spliceTo}
                </div>
              )}
          </div>
        );
      })}
    </div>
  ) : null;
};

const ResidentDevicesStatuses = ({
  devicesMinimized,
  residentId,
  isAway,
  onClickDeviceStatuses,
  size,
}: {
  devicesMinimized: DeviceMinimizedMap;
  residentId: string;
  isAway: boolean;
  size?: number;
  onClickDeviceStatuses: (e: any) => void;
}): JSX.Element => {
  const selection = useMemo(() => doesAnyDeviceHasProblemCreator(), []);
  const hasProblems = useSelector((state: IAppState) =>
    selection(state, devicesMinimized)
  );
  return (
    Object.keys(devicesMinimized).length > 0 && (
      <i
        className={`icon-info icon-big ${
          hasProblems ? "red" : ""
        } font-${size}`}
        onClick={onClickDeviceStatuses}
      />
    )
  );
};

export default React.memo(OneStatusView);
